import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Check } from "lucide-react";
import { useAuth } from "../lib/auth";
import { PLANS } from "../data/plans";

/**
 * Shared pricing grid used by the landing page (#pricing section)
 * and the dedicated /pricing page. Reads plan data from /data/plans.js.
 *
 * The card renders the short `name` (Starter / Professional / Enterprise)
 * as an overline for visual continuity, and the outcome-focused `title`
 * ("Launch Your Consulting Business", etc.) as the primary card heading.
 */
export default function PricingSection({ heading, subheading, eyebrow = "Pricing", id = "pricing" }) {
  const nav = useNavigate();
  const { user } = useAuth();

  const headingNode = heading ?? (
    <>Attract, assess, nurture, convert. <span className="italic text-accent">All from one platform.</span></>
  );
  const subheadingText =
    subheading ??
    "Everything you need to attract, assess, nurture, convert and grow consulting clients from one platform. Monthly or lifetime — change or cancel any time.";

  return (
    <section id={id} className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="text-center max-w-2xl mx-auto">
        <div className="overline justify-center">{eyebrow}</div>
        <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">{headingNode}</h2>
        <p className="text-muted-foreground mt-4">{subheadingText}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {PLANS.map((p) => (
          <Card
            key={p.id}
            className={`p-8 card-elevated relative ${p.popular ? "ring-1 ring-accent" : ""}`}
            data-testid={`plan-${p.id}`}
          >
            {p.popular && (
              <div className="absolute -top-3 right-6 bg-accent text-accent-foreground text-xs px-3 py-1 rounded-full font-medium">
                Most popular
              </div>
            )}
            <div className="overline">{p.name}</div>
            {p.title && (
              <h3
                className="font-display text-2xl leading-tight mt-3"
                data-testid={`plan-title-${p.id}`}
              >
                {p.title}
              </h3>
            )}
            {p.tagline && (
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.tagline}</p>
            )}
            <div className="mt-5 flex items-end gap-2">
              <div className="font-display text-5xl">${p.monthly}</div>
              <div className="text-muted-foreground pb-2">/month</div>
            </div>
            <div className="text-sm text-muted-foreground mt-1">or ${p.lifetime} lifetime</div>
            <ul className="mt-6 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={`plan-cta-${p.id}`}
              className={`w-full mt-7 rounded-full ${p.popular ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
              variant={p.popular ? "default" : "outline"}
              onClick={() => nav(user?.role === "consultant" ? "/dashboard?tab=billing" : "/register")}
            >
              {p.ctaLabel || `Get ${p.name}`}
            </Button>
          </Card>
        ))}
      </div>
    </section>
  );
}
